import {useCallback, useEffect, useRef, useState} from "react";
import {useFilters} from "context/FilterContext";

// Google Maps is loaded by the location field (react-google-places-autocomplete).
const waitForGeocoder = (timeoutMs = 5000) =>
    new Promise((resolve) => {
        const started = Date.now();
        const check = () => {
            if (window.google?.maps?.Geocoder) return resolve(new window.google.maps.Geocoder());
            if (Date.now() - started > timeoutMs) return resolve(null);
            setTimeout(check, 150);
        };
        check();
    });

// "City, ST, USA" like the place suggestions, or null.
const readableLocation = async (lat, lng) => {
    const geocoder = await waitForGeocoder();
    if (!geocoder) return null;
    try {
        const {results} = await geocoder.geocode({location: {lat, lng}});
        const pick = (type) => results?.find((r) => r.types?.includes(type));
        const best = pick("locality") || pick("sublocality") || pick("postal_code") || pick("administrative_area_level_2") || results?.[0];
        return best?.formatted_address || null;
    } catch {
        return null;
    }
};

const getPosition = () =>
    new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(Object.assign(new Error("unsupported"), {code: "unsupported"}));
        navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 12000,
            maximumAge: 10 * 60 * 1000,
        });
    });

const MESSAGES = {
    denied: "Location access is off. Type your city instead.",
    unavailable: "We couldn't find your location. Type your city instead.",
};

/**
 * Fills the search location from the browser's location. The permission
 * prompt only appears after the visitor asks for it (locate()). When access
 * was already granted, fillIfAllowed() fills an empty field without a prompt.
 */
export const useCurrentLocation = () => {
    const {filters, setFilters} = useFilters();
    const [status, setStatus] = useState("idle"); // idle | locating | done | denied | unavailable
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    const locate = useCallback(async () => {
        setStatus("locating");
        try {
            const {coords} = await getPosition();
            const label = (await readableLocation(coords.latitude, coords.longitude)) || "Current location";
            setFilters({location: label, coordinates: {latitude: coords.latitude, longitude: coords.longitude}});
            if (mounted.current) setStatus("done");
        } catch (err) {
            // 1 = PERMISSION_DENIED
            if (mounted.current) setStatus(err?.code === 1 ? "denied" : "unavailable");
        }
    }, [setFilters]);

    const fillIfAllowed = useCallback(async () => {
        if (filters.location || status === "locating" || !navigator.permissions?.query) return;
        try {
            const p = await navigator.permissions.query({name: "geolocation"});
            if (p.state === "granted") locate();
        } catch {
            // Permissions API not available: wait for an explicit tap.
        }
    }, [filters.location, status, locate]);

    return {locate, fillIfAllowed, status, message: MESSAGES[status] || ""};
};
